// Unread-SMS badge on the Admin page (AdminPage.fetchTotalUnreadMessages).
//
// The badge used to send one SMS query per listed patient and await each in
// turn: 45-92 round trips (6.6-13.3 s) on a busy day. Here each schema's
// phones go out together, one `from ILIKE %phone%` OR filter per chunk, every
// chunk at once, and each message is handed back to every patient whose phone
// it contains: the substring test the per-patient ILIKE made in SQL. Totals,
// per-patient lists (newest first) and per-schema routing stay as they were.

// Each phone adds ~30 URL-encoded characters to the `or=(...)` parameter; 50
// keeps a request's URL near 1.5 KB.
export const UNREAD_PHONE_CHUNK_SIZE = 50;

// PostgREST caps a response at 1000 rows. Each per-patient query had that cap
// to itself; a chunk's phones share one, so page past it.
export const UNREAD_PAGE_SIZE = 1000;

// Digits only ("" when none are left). Phones are spliced into a PostgREST
// or() string, where a comma, parenthesis or quote would break the filter and
// "%", "*" or "_" would widen the match.
export const unreadPhoneDigits = (phone) => String(phone ?? "").replace(/\D/g, "");

// The per-patient query's routing: the schema the row was fanned in from
// (`_schema`), else the viewer's.
const schemaOf = (patient, fallbackSchema) =>
  patient?.patientData?._schema || fallbackSchema || "public";

const poolKey = (schema, phone) => `${schema}\u0000${phone}`;

// Every number a patient can text from (cell, home -- utils/smsPhone), as
// digits; `phones` when the badge supplied it, else the single `phone`.
const patientDigits = (patient) => {
  const list = patient?.phones?.length ? patient.phones : [patient?.phone];
  return [...new Set(list.map(unreadPhoneDigits).filter(Boolean))];
};

const newestFirst = (a, b) =>
  String(b?.created_at ?? "").localeCompare(String(a?.created_at ?? "")) ||
  String(b?.id ?? "").localeCompare(String(a?.id ?? ""), undefined, { numeric: true });

/**
 * Each schema's distinct phones, at most `size` per chunk: [{ schema, phones }].
 * `patientPhones` is the badge's list, [{ phone, patientData, ... }].
 */
export function chunkUnreadPhones(patientPhones, fallbackSchema, size = UNREAD_PHONE_CHUNK_SIZE) {
  const bySchema = new Map();
  (patientPhones || []).forEach((patient) => {
    const phones = patientDigits(patient);
    if (phones.length === 0) return;
    const schema = schemaOf(patient, fallbackSchema);
    if (!bySchema.has(schema)) bySchema.set(schema, new Set());
    phones.forEach((phone) => bySchema.get(schema).add(phone));
  });
  const chunks = [];
  bySchema.forEach((phoneSet, schema) => {
    const phones = [...phoneSet];
    for (let i = 0; i < phones.length; i += size) {
      chunks.push({ schema, phones: phones.slice(i, i + size) });
    }
  });
  return chunks;
}

// `from.ilike.%<digits>%` per phone, OR'ed by PostgREST.
export const unreadPhonesOrFilter = (phones) =>
  (phones || [])
    .map(unreadPhoneDigits)
    .filter(Boolean)
    .map((phone) => `from.ilike.%${phone}%`)
    .join(",");

// One chunk's unread inbound messages, newest first, every page. null when a
// page fails, so that chunk's patients count nothing, as a failed
// per-patient query did; a thrown error still reaches the caller's catch.
async function fetchUnreadChunk(client, { schema, phones }) {
  const rows = [];
  for (let from = 0; ; from += UNREAD_PAGE_SIZE) {
    const { data, error } = await client
      .schema(schema)
      .from("SMS")
      .select("id, message, created_at, from")
      .eq("direction", "inbound")
      .eq("reviewed", false)
      .or(unreadPhonesOrFilter(phones))
      .order("created_at", { ascending: false })
      .order("id", { ascending: false }) // ties on created_at must not shift between pages
      .range(from, from + UNREAD_PAGE_SIZE - 1);
    if (error) return null;
    rows.push(...(data || []));
    if (!data || data.length < UNREAD_PAGE_SIZE) return rows;
  }
}

/**
 * Hand each chunk's messages back to the patients whose phone they contain.
 * Returns { total, entries } in the shape the badge popover reads: one
 * `{ ...patient, messages, unreadCount }` per patient with unread messages,
 * in `patientPhones` order, messages newest first. A message containing two
 * patients' phones counts once for each, as two per-patient queries did; a
 * patient's own cell and home numbers count a message once.
 */
export function assignUnreadMessages(patientPhones, chunkResults, fallbackSchema) {
  const pools = new Map();
  (chunkResults || []).forEach(({ schema, phones, messages }) => {
    (phones || []).forEach((phone) => pools.set(poolKey(schema, phone), messages));
  });
  let total = 0;
  const entries = [];
  (patientPhones || []).forEach((patient) => {
    const phones = patientDigits(patient);
    const schema = schemaOf(patient, fallbackSchema);
    const patientPools = [...new Set(phones.map((phone) => pools.get(poolKey(schema, phone))).filter(Boolean))];
    if (patientPools.length === 0) return;
    const byId = new Map();
    patientPools.forEach((pool) => pool.forEach((msg) => {
      const from = String(msg?.from ?? "");
      if (phones.some((phone) => from.includes(phone))) byId.set(msg?.id ?? msg, msg);
    }));
    const messages = [...byId.values()];
    if (messages.length === 0) return;
    if (patientPools.length > 1) messages.sort(newestFirst);
    total += messages.length;
    entries.push({ ...patient, messages, unreadCount: messages.length });
  });
  return { total, entries };
}

/**
 * The whole badge: `patientPhones` ([{ phone, patientName, patientId,
 * patientData }]) in, { total, entries } out. All chunks run at once.
 */
export async function fetchUnreadMessages(client, patientPhones, fallbackSchema) {
  const chunks = chunkUnreadPhones(patientPhones, fallbackSchema);
  const results = await Promise.all(
    chunks.map(async (chunk) => ({ ...chunk, messages: await fetchUnreadChunk(client, chunk) })),
  );
  return assignUnreadMessages(patientPhones, results, fallbackSchema);
}
